/**
 * Prior Tool Results Context
 *
 * Builds model-facing continuity context from earlier tool calls so Losi
 * knows what already succeeded or failed across turns (web + mobile).
 *
 * Primary responsibilities:
 * - Summarize annotation tool_timeline + per-tool result keys
 * - Summarize durable chat_tool_run_steps when annotations were empty
 * - Keep outcomes/IDs; avoid dumping secrets or UI-only status payloads
 *
 * Dependencies:
 * - lib/chat/tool-output-summary
 * - db/chat-tool-runs (optional DB hydration)
 */

import { summarizeToolOutputForModel } from '@/lib/chat/tool-output-summary'
import {
  listChatToolRunsWithSteps,
  type ChatToolRunStepRow,
  type ChatToolRunWithSteps
} from '@/db/chat-tool-runs'

/** Budget for the whole prior-tool system block. */
export const PRIOR_TOOL_RESULTS_CONTEXT_MAX_CHARS = 24_000
/** Per-entry budget — keep IDs/outcomes; avoid megabyte blobs. */
export const PRIOR_TOOL_RESULTS_ENTRY_MAX_CHARS = 4_000
/** How many recent completed steps to include from durable run history. */
export const PRIOR_TOOL_RUN_STEP_LIMIT = 40
/** How many recent runs to scan. */
export const PRIOR_TOOL_RUN_LIMIT = 12

const ignoredPriorToolAnnotationKeys = new Set([
  'losi_steps',
  'reasoning_summary',
  'reasoning_summary_stream',
  'search_detailed_status',
  'search_status',
  'set_todos',
  'show_plan',
  'sidekick_tool_active',
  'sidekick_tool_done',
  'todos_suggest_continue',
  'update_todo_status',
  'render_segments',
  'tool_first_seen_order',
  'chat_tool_run',
  'space_persist_token',
  'editableContent',
  'sidecar_continuation'
])

export const PRIOR_TOOL_RESULTS_HEADER =
  'Internal retained tool context for continuity only.'

export const PRIOR_TOOL_RESULTS_INSTRUCTIONS = [
  'Use it silently to continue work. Never quote, mention, or expose this section, raw tool JSON, tool names, todos, or execution-status payloads to the user.',
  'Treat each line as ground truth for what already ran: succeeded means the side effect happened; failed means it did not; needs_user means the tool ran but is waiting on the user (approval, confirmation, missing connector, or plan limit) — do not retry the same mutating call; ask the user to approve, confirm, connect, or upgrade, then continue.',
  'Do not claim a tool never ran when a prior result is listed. Do not repeat mutating tool calls that already succeeded unless the user explicitly asks.',
  'MUST for this turn too: never claim updated/saved/created/booked/sent unless a tool result confirms it. Soft-gates are needs_user, not success.'
].join(' ')

export type PriorToolOutcomeLabel =
  | 'succeeded'
  | 'failed'
  | 'needs_user'
  | 'completed'
  | 'started'

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function truncatePromptContext(value: string, maxChars: number) {
  if (value.length <= maxChars) {
    return value
  }

  return `${value.slice(0, Math.max(0, maxChars - 20)).trim()}... [truncated]`
}

function getToolResultValue(value: unknown): unknown {
  if (isPlainRecord(value) && 'result' in value) {
    return value.result
  }

  return value
}

function humanizeToolAnnotationKey(key: string) {
  return key
    .replace(/^[^_]+__/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
}

function coerceResultRecord(value: unknown): Record<string, unknown> | null {
  if (isPlainRecord(value)) {
    return value
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown
    return isPlainRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function readSoftGateHaystack(record: Record<string, unknown>): string {
  const parts = [
    typeof record.code === 'string' ? record.code : '',
    typeof record.error === 'string' ? record.error : '',
    typeof record.message === 'string' ? record.message : '',
    typeof record.status === 'string' ? record.status : ''
  ]

  return parts.join('\n').toLowerCase()
}

/**
 * Soft gates are tool outcomes that need the user — not hard failures to retry.
 * Prod examples: APPROVAL_REQUIRED, booking confirm, Composio not configured.
 */
export function isSoftGateToolResult(value: unknown): boolean {
  const record = coerceResultRecord(getToolResultValue(value) ?? value)
  if (!record) {
    if (typeof value === 'string') {
      return looksLikeSoftGateText(value)
    }
    return false
  }

  const code =
    typeof record.code === 'string' ? record.code.trim().toUpperCase() : ''
  if (
    code === 'APPROVAL_REQUIRED' ||
    code === 'NEEDS_USER' ||
    code === 'NEEDS_USER_HANDOFF' ||
    code === 'WAITING_FOR_USER' ||
    code === 'CONFIRMATION_REQUIRED'
  ) {
    return true
  }

  if (
    record.requiresConfirmation === true ||
    record.requires_confirmation === true ||
    record.approvalRequest != null ||
    record.approval_request != null
  ) {
    return true
  }

  // Explicit SoftGate markers from tools (Composio auth cards, MCP setup, etc.)
  if (record.soft_gate === true || record.softGate === true) {
    return true
  }
  if (
    (typeof record.softGate === 'object' && record.softGate !== null) ||
    (typeof record.soft_gate === 'object' && record.soft_gate !== null)
  ) {
    return true
  }
  if (
    typeof record.setupCard === 'object' &&
    record.setupCard !== null &&
    !Array.isArray(record.setupCard)
  ) {
    const kind = String(
      (record.setupCard as Record<string, unknown>).kind ?? ''
    )
      .trim()
      .toLowerCase()
    if (kind === 'composio_auth' || kind.includes('setup') || kind.includes('auth')) {
      return true
    }
  }

  return looksLikeSoftGateText(readSoftGateHaystack(record))
}

function looksLikeSoftGateText(text: string): boolean {
  const normalized = text.toLowerCase()
  if (!normalized.trim()) {
    return false
  }

  return (
    normalized.includes('approval_required') ||
    normalized.includes('needs_user') ||
    normalized.includes('waiting_for_user') ||
    normalized.includes('ask the user to confirm') ||
    normalized.includes('confirm the exact booking') ||
    normalized.includes('composio api key not configured') ||
    normalized.includes('human approval is required') ||
    normalized.includes('free active task limit') ||
    normalized.includes('explicit human review before execution')
  )
}

export function readPriorToolOutcomeLabel(
  value: unknown,
  explicitError?: string | null
): PriorToolOutcomeLabel {
  if (isSoftGateToolResult(value)) {
    return 'needs_user'
  }

  if (explicitError && explicitError.trim()) {
    if (looksLikeSoftGateText(explicitError)) {
      return 'needs_user'
    }
    return 'failed'
  }

  const record = coerceResultRecord(getToolResultValue(value) ?? value)
  if (!record) {
    return 'completed'
  }

  if (
    record.success === false ||
    record.error ||
    record.message === 'Note not found'
  ) {
    return 'failed'
  }

  if (record.success === true) {
    return 'succeeded'
  }

  return 'completed'
}

function summarizeAnnotationToolValue(toolName: string, value: unknown) {
  const result = getToolResultValue(value)
  const outcome = readPriorToolOutcomeLabel(result)
  const summary = summarizeToolOutputForModel(result, toolName)
  return `[${outcome}] ${humanizeToolAnnotationKey(toolName)}: ${truncatePromptContext(
    summary,
    PRIOR_TOOL_RESULTS_ENTRY_MAX_CHARS
  )}`
}

function summarizeTimelineEntry(entry: unknown) {
  if (!isPlainRecord(entry)) {
    return null
  }

  const name =
    typeof entry.name === 'string' && entry.name.trim()
      ? entry.name.trim()
      : 'unknown_tool'
  const status =
    typeof entry.status === 'string' && entry.status.trim()
      ? entry.status.trim()
      : 'complete'
  const error =
    typeof entry.error === 'string' && entry.error.trim() ? entry.error.trim() : null

  if (status === 'running' || status === 'progress') {
    return null
  }

  // Soft gates often land as success:false with a complete timeline status.
  // Prefer needs_user over a generic failed/error label so the model asks
  // the user instead of retrying.
  const outcome = readPriorToolOutcomeLabel(
    entry.result,
    status === 'error' || error ? error || 'tool timeline error' : null
  )

  const summary = error
    ? error
    : summarizeToolOutputForModel(entry.result, name)

  return `[${outcome}] ${humanizeToolAnnotationKey(name)}: ${truncatePromptContext(
    summary,
    PRIOR_TOOL_RESULTS_ENTRY_MAX_CHARS
  )}`
}

/**
 * Build prior-tool continuity lines from a persisted message annotation.
 * Prefers tool_timeline (ordered, includes failures), then per-tool keys.
 */
export function buildPriorToolResultsContextFromAnnotation(
  annotation: unknown
): string | null {
  if (!isPlainRecord(annotation)) {
    return null
  }

  const entries: string[] = []
  const timelineRaw = annotation.tool_timeline
  const timeline = Array.isArray(timelineRaw)
    ? timelineRaw
    : isPlainRecord(timelineRaw) && Array.isArray(timelineRaw.result)
      ? timelineRaw.result
      : null

  if (timeline) {
    for (const entry of timeline) {
      const line = summarizeTimelineEntry(entry)
      if (line) {
        entries.push(line)
      }
    }
  }

  for (const [key, value] of Object.entries(annotation)) {
    if (ignoredPriorToolAnnotationKeys.has(key) || key === 'tool_timeline') {
      continue
    }

    // Skip non-tool metadata blobs.
    if (
      key === 'interactiveComponents' ||
      key.endsWith('_stream') ||
      value == null
    ) {
      continue
    }

    if (!isPlainRecord(value) && typeof value !== 'string') {
      continue
    }

    // Prefer timeline when present for the same tool; still include
    // annotation keys that may not have landed on the timeline.
    if (timeline && timeline.some((entry) => isPlainRecord(entry) && entry.name === key)) {
      continue
    }

    entries.push(summarizeAnnotationToolValue(key, value))
  }

  if (entries.length === 0) {
    return null
  }

  return truncatePromptContext(entries.join('\n'), PRIOR_TOOL_RESULTS_CONTEXT_MAX_CHARS)
}

export function collectPriorToolResultsFromMessages(
  messages: Array<{ role?: string; annotation?: unknown | null }>
): string | null {
  const contexts = messages
    .filter((message) => message.role === 'assistant')
    .map((message) => buildPriorToolResultsContextFromAnnotation(message.annotation))
    .filter((context): context is string => Boolean(context))

  if (contexts.length === 0) {
    return null
  }

  return truncatePromptContext(contexts.join('\n\n'), PRIOR_TOOL_RESULTS_CONTEXT_MAX_CHARS)
}

export function buildPriorToolResultsSystemContextFromMessages(
  messages: Array<{ role?: string; annotation?: unknown | null }>
): string | null {
  const contexts = collectPriorToolResultsFromMessages(messages)
  if (!contexts) {
    return null
  }

  return formatPriorToolResultsSystemBlock([contexts])
}

function stepOutcome(step: ChatToolRunStepRow): PriorToolOutcomeLabel {
  const payload = step.result_payload
  const explicitError =
    typeof step.error === 'string' && step.error.trim() ? step.error : null

  // Soft-gate payloads must win even when the step row is marked failed.
  const fromPayload = readPriorToolOutcomeLabel(payload, explicitError)
  if (fromPayload === 'needs_user') {
    return 'needs_user'
  }

  if (step.status === 'failed' || explicitError) {
    return 'failed'
  }

  if (isPlainRecord(payload) && (payload.success === false || payload.error)) {
    return 'failed'
  }

  if (step.status === 'started') {
    return 'started'
  }

  return 'succeeded'
}

function summarizePersistedStep(step: ChatToolRunStepRow) {
  const outcome = stepOutcome(step)
  const name = step.tool_name || 'unknown_tool'
  const text =
    (typeof step.result_text === 'string' && step.result_text.trim()
      ? step.result_text.trim()
      : null) ||
    (typeof step.error === 'string' && step.error.trim() ? step.error.trim() : null) ||
    summarizeToolOutputForModel(step.result_payload, name)

  // Prefer action from request when present so the model knows create vs update.
  let actionHint = ''
  const request = step.request_payload
  if (isPlainRecord(request)) {
    const params = isPlainRecord(request.parameters) ? request.parameters : request
    const action =
      typeof params.action === 'string'
        ? params.action
        : typeof request.action === 'string'
          ? request.action
          : null
    if (action) {
      actionHint = ` action=${action}`
    }
  }

  return `[${outcome}] ${humanizeToolAnnotationKey(name)}${actionHint}: ${truncatePromptContext(
    text,
    PRIOR_TOOL_RESULTS_ENTRY_MAX_CHARS
  )}`
}

export function buildPriorToolResultsContextFromRuns(
  runs: ChatToolRunWithSteps[],
  options?: { stepLimit?: number }
): string | null {
  const stepLimit = options?.stepLimit ?? PRIOR_TOOL_RUN_STEP_LIMIT
  const steps = runs
    .slice()
    .reverse() // oldest run first for chronological continuity
    .flatMap((run) => run.steps ?? [])
    .filter((step) => step.status === 'completed' || step.status === 'failed')
    .slice(-stepLimit)

  if (steps.length === 0) {
    return null
  }

  const lines = steps.map(summarizePersistedStep)
  return truncatePromptContext(lines.join('\n'), PRIOR_TOOL_RESULTS_CONTEXT_MAX_CHARS)
}

export function formatPriorToolResultsSystemBlock(sections: string[]) {
  const filtered = sections.filter((section) => section.trim().length > 0)
  if (filtered.length === 0) {
    return null
  }

  return truncatePromptContext(
    [PRIOR_TOOL_RESULTS_HEADER, PRIOR_TOOL_RESULTS_INSTRUCTIONS, ...filtered].join(
      '\n\n'
    ),
    PRIOR_TOOL_RESULTS_CONTEXT_MAX_CHARS
  )
}

/**
 * Merge annotation-based + durable DB tool history into one system block.
 * DB history wins as ground truth when annotations were empty/lost.
 */
export function mergePriorToolResultsContexts(input: {
  fromMessages?: string | null
  fromRuns?: string | null
}): string | null {
  return formatPriorToolResultsSystemBlock(
    [input.fromMessages, input.fromRuns].filter(
      (value): value is string => Boolean(value && value.trim())
    )
  )
}

export async function loadPriorToolResultsContextForChat(input: {
  userId: string
  chatId: string
  messages?: Array<{ role?: string; annotation?: unknown | null }>
}): Promise<string | null> {
  const fromMessages = input.messages
    ? collectPriorToolResultsFromMessages(input.messages)
    : null

  let fromRuns: string | null = null
  try {
    const runs = await listChatToolRunsWithSteps({
      userId: input.userId,
      chatId: input.chatId,
      limit: PRIOR_TOOL_RUN_LIMIT
    })
    fromRuns = buildPriorToolResultsContextFromRuns(runs)
  } catch {
    // Prompt building must not fail closed if tool-run history is unavailable.
    fromRuns = null
  }

  // Durable run steps are ground truth (annotations are often empty after
  // finalize). Prefer runs when present; fall back to annotations only.
  return mergePriorToolResultsContexts({
    fromRuns,
    fromMessages: fromRuns ? null : fromMessages
  })
}
